import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useIdelinkTheme } from '../../../configs/IdeLinkTheme';
import { ItemState } from '../../../services/commons/ItemState';
import { AdminPermission } from '../../../services/commons/Me';
import { AdminPermissions, AdminPermissionsOnModule, } from '../../../services/commons/Permissions/AdminPermissions';
import {
    ContentPermission,
    UserPermissions,
    UserPermissionsOnContent,
} from '../../../services/commons/Permissions/ContentPermission';
import { PermissionsAbilityHelper } from '../../../services/commons/Permissions/PermissionsAbilityHelper';
import { PermissionType } from '../../../services/commons/Permissions/PermissionType';
import { TabPermissionItem } from '../../BackUsers/Pages/components/PermissionsTreeContainer/PermissionsTreeContainer';

export interface UserPermissionsOnEvent extends UserPermissionsOnContent {
    // *QUESTIONS
    readonly canAddEventQuestions?: boolean;
    readonly canRemoveEventQuestions?: boolean;
    readonly canDisabledAddEventQuestions?: boolean;
    readonly canAddAnswers?: boolean;
    readonly canUpdateQuestionsOrder?: boolean;

    readonly canEditQuestionTitle?: boolean;
    readonly canEditQuestionType?: boolean;
    readonly canEditQuestionExternalReference?: boolean;
    readonly canEditQuestionLabel?: boolean;
    readonly canEditQuestionDescription?: boolean;

    //* QUESTION - ANSWERS
    readonly canEditAnswerTitle?: boolean;
    readonly canEditAnswerActionType?: boolean;
    readonly canEditAnswerExternalReference?: boolean;
    readonly canRemoveAnswer?: boolean;

    //* EVENT DATES
    canAddEventDate?: boolean;
    addEventDateEnabled?: boolean;
    removeEventDateEnabled?: boolean;
    canRemoveEventDate?: boolean;

    startDateEventDateEnabled?: boolean;
    startHourEventDateEnabled?: boolean;
    endDateEventDateEnabled?: boolean;
    endHourEventDateEnabled?: boolean;

    canAddAddressEventDate?: boolean;
    addAddressEventDateEnabled?: boolean;
    addSameAddressEventDateEnabled?: boolean;

    coordinatesEventDateEnabled?: boolean;

    eventDateNameEnabled?: boolean;
    eventDateAddressEnabled?: boolean;
    eventDateZipCodeEnabled?: boolean;
    eventDateCityEnabled?: boolean;
    eventDateLatitudeEnabled?: boolean;
    eventDateLongitudeEnabled?: boolean;
}

export class EventsPermissions {
    public static EVENTS_TAB_HASH = '#rights-events';
    public static EVENTS_CODE = 'Events';

    public static Events: AdminPermissionsOnModule = {};
    public static Reminders: AdminPermissionsOnModule = {};

    public static getEventsTab = (): TabPermissionItem => ({
        hash: EventsPermissions.EVENTS_TAB_HASH,
        label: <FormattedMessage id="backofficeUsers.create.tabs.permissions.sectionTabs.events" />,
        getIcon: () => useIdelinkTheme().events.permissionIcon,
        getIconSelected: () => useIdelinkTheme().events.permissionSelectedIcon,
    });

    public static initializeEventsAndRemindersAdminPermissions = (
        perms: Array<AdminPermission> = []
    ) => {
        const permsOnEvents = AdminPermissions.filter(perms, EventsPermissions.EVENTS_CODE);

        EventsPermissions.Events = AdminPermissions.toPermissions(permsOnEvents);
        PermissionsAbilityHelper.logPermissions('Events', EventsPermissions.Events);

        EventsPermissions.Reminders = AdminPermissions.toPermissions(permsOnEvents);
        PermissionsAbilityHelper.logPermissions('Reminders', EventsPermissions.Reminders);
    };

    public static toDefaultEventPermisisons = (): UserPermissionsOnEvent => {
        const rawPermissions: UserPermissionsOnEvent = EventsPermissions.toUserPermissionsOnEvent(
            ItemState.Draft
        );
        const permissions: { [field: string]: boolean } = {};
        Object.keys(rawPermissions).forEach((key) => (permissions[key] = true));
        return permissions as UserPermissionsOnEvent;
    };

    public static toUserPermissionsOnEvent = (
        contentState: ItemState,
        contentPermission?: ContentPermission
    ): UserPermissionsOnEvent => {
        const canEdit = UserPermissions.canEdit(contentState, contentPermission);
        const isDraft =
            contentState === ItemState.Draft &&
            UserPermissions.has(PermissionType.ContentEditDraft, contentPermission);

        const isPublishedOrUnpublished =
            (contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
            (contentState === ItemState.Unpublished &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission));

        const isReadDraftOrPublished =
            (contentState === ItemState.Draft &&
                UserPermissions.has(PermissionType.ContentReadDraft, contentPermission)) ||
            (contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission));

        return {
            ...UserPermissions.toUserPermissionsOnContent(contentState, contentPermission),
            canEditPdf: canEdit,
            //* QUESTIONS
            canAddEventQuestions: canEdit,
            canRemoveEventQuestions: canEdit,
            canAddAnswers: canEdit,
            canUpdateQuestionsOrder: canEdit,

            canEditQuestionTitle: canEdit,
            canEditQuestionType: canEdit,
            canEditQuestionExternalReference: canEdit,
            canEditQuestionLabel: canEdit,
            canEditQuestionDescription: canEdit,
            //* QUESTIONS - ANSWERS
            canEditAnswerTitle: canEdit,
            canEditAnswerActionType: canEdit,
            canEditAnswerExternalReference: canEdit,
            canRemoveAnswer: canEdit,

            //* EVENT DATES
            canAddEventDate: isDraft,
            addEventDateEnabled: !isPublishedOrUnpublished,
            canRemoveEventDate: isDraft,
            removeEventDateEnabled: !isPublishedOrUnpublished,

            startDateEventDateEnabled: isDraft,
            startHourEventDateEnabled: isDraft,
            endDateEventDateEnabled: isDraft,
            endHourEventDateEnabled: isDraft,

            canAddAddressEventDate: isDraft,
            addAddressEventDateEnabled: !isReadDraftOrPublished,
            addSameAddressEventDateEnabled: isDraft,

            coordinatesEventDateEnabled: isDraft,
            eventDateNameEnabled: isDraft,
            eventDateAddressEnabled: isDraft,
            eventDateZipCodeEnabled: isDraft,
            eventDateCityEnabled: isDraft,
            eventDateLatitudeEnabled: isDraft,
            eventDateLongitudeEnabled: isDraft,
        };
    };
}
